// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, AlertCircle, Loader2, ShieldCheck } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'

/**
 * /admin/pharmacies/new — single-form onboarding wizard.
 *
 * Records pharmacy details + BAA reference + COO approval in one shot.
 * On success, redirects to the new pharmacy's detail page where the admin
 * can invite the primary admin and generate ingest credentials.
 *
 * Two pieces of compliance metadata are required and have prominent UI:
 *   1. BAA reference — contract id, document number, or similar that
 *      points to the executed BAA. Cannot be created without this.
 *   2. COO (or equivalent) approval — name + email of the person at the
 *      pharmacy who authorized the engagement. Recorded for audit.
 *
 * SSO is captured but not configured here. The actual SSO setup happens
 * after pharmacy creation.
 */
// Draft persistence: the onboarding form is long, and a post-create display error
// (or an accidental reload) used to wipe everything. We auto-save the form to
// localStorage on every change and restore it on return, clearing it only once a
// pharmacy is actually created.
const DRAFT_KEY = 'owlrx:pharmacy-onboarding-draft'

const EMPTY_FORM = {
  name: '',
  npi: '',
  ncpdp: '',
  dea: '',
  email: '',
  primaryPhone: '',
  addressLine: '',
  city: '',
  stateCode: '',
  zipCode: '',
  baaReference: '',
  baaEffectiveDate: '',
  baaExpiryDate: '',
  cooApproverName: '',
  cooApproverEmail: '',
  ssoProvider: 'none' as 'none' | 'okta' | 'azure_ad' | 'google_workspace',
  // WI-820 — multi-branch parent organization
  parentOrgMode: 'standalone' as 'standalone' | 'existing' | 'new',
  parentOrganizationId: '',
  newParentOrganizationName: '',
  branchLabel: '',
  sendingDomain: '',
}

export default function PharmacyOnboardingWizard() {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY)
      if (saved) {
        const d = JSON.parse(saved)
        if (d && typeof d === 'object') return { ...EMPTY_FORM, ...(d as Partial<typeof EMPTY_FORM>) }
      }
    } catch { /* ignore unavailable/corrupt storage */ }
    return { ...EMPTY_FORM }
  })
  const [draftRestored, setDraftRestored] = useState(() => {
    try { return !!localStorage.getItem(DRAFT_KEY) } catch { return false }
  })
  const [parentOrgs, setParentOrgs] = useState<api.ParentOrganization[]>([])
  useEffect(() => {
    api.listParentOrgs().then(r => setParentOrgs(r.items)).catch(() => setParentOrgs([]))
  }, [])

  // Auto-save the draft on every change.
  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)) } catch { /* quota/private mode */ }
  }, [form])

  function discardDraft() {
    try { localStorage.removeItem(DRAFT_KEY) } catch { /* ignore */ }
    setForm({ ...EMPTY_FORM })
    setDraftRestored(false)
  }

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm(s => ({ ...s, [k]: v }))
    if (draftRestored) setDraftRestored(false)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null); setSubmitting(true)
    try {
      const r = await api.createPharmacy({
        name: form.name,
        npi: form.npi || undefined,
        ncpdp: form.ncpdp || undefined,
        dea: form.dea || undefined,
        email: form.email || undefined,
        primaryPhone: form.primaryPhone || undefined,
        addressLine: form.addressLine || undefined,
        city: form.city || undefined,
        stateCode: form.stateCode || undefined,
        zipCode: form.zipCode || undefined,
        baaReference: form.baaReference,
        baaEffectiveDate: form.baaEffectiveDate || undefined,
        baaExpiryDate: form.baaExpiryDate || undefined,
        cooApproverName: form.cooApproverName,
        cooApproverEmail: form.cooApproverEmail,
        ssoProvider: form.ssoProvider,
        parentOrganizationId: form.parentOrgMode === 'existing' ? form.parentOrganizationId : undefined,
        newParentOrganizationName: form.parentOrgMode === 'new' ? form.newParentOrganizationName : undefined,
        branchLabel: form.parentOrgMode !== 'standalone' ? (form.branchLabel || undefined) : undefined,
      })
      // Created successfully — drop the draft so we don't restore stale data next time.
      try { localStorage.removeItem(DRAFT_KEY) } catch { /* ignore */ }
      const domain = form.sendingDomain.trim()
      navigate(`/admin/pharmacies/${r.id}${domain ? `?provisionDomain=${encodeURIComponent(domain)}` : ''}`)
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setSubmitting(false)
    }
  }

  // WI-897: tell the operator exactly which required field is missing so
  // they don't stare at a disabled button wondering why it does nothing.
  const missing: string[] = []
  if (!form.name.trim())             missing.push('Pharmacy name')
  if (!form.baaReference.trim())     missing.push('BAA reference')
  if (!form.cooApproverName.trim())  missing.push('COO approver name')
  if (!form.cooApproverEmail.trim()) missing.push('COO approver email')
  if (form.parentOrgMode === 'existing' && !form.parentOrganizationId)
    missing.push('Parent organization (or switch to standalone)')
  if (form.parentOrgMode === 'new' && !form.newParentOrganizationName.trim())
    missing.push('New parent organization name (or switch to standalone)')

  const canSubmit = missing.length === 0 && !submitting

  return (
    <div style={{ maxWidth: '100%' }}>
      <button onClick={() => navigate('/admin/pharmacies')} style={backLinkStyle}>
        <ChevronLeft size={14} /> Back to pharmacies
      </button>
      <h2 style={h2Style}>Onboard a new pharmacy</h2>
      <p style={subtitleStyle}>
        Records pharmacy details, BAA reference, and the approval from their
        authorized signer. After creation, you'll invite the pharmacy's
        primary admin and generate their PioneerRx ingest credentials.
      </p>

      {draftRestored && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', marginBottom: 16, borderRadius: 8, background: '#f0f7ff', border: '1px solid #cfe3ff', fontSize: 13 }}>
          <span>We restored your unsaved draft — it auto-saves as you type, so an error won't lose it.</span>
          <button type="button" onClick={discardDraft} style={{ ...secondaryBtnStyle, padding: '4px 10px', whiteSpace: 'nowrap' }}>
            Discard draft
          </button>
        </div>
      )}

      <form onSubmit={submit}>
        {/* Parent organization (WI-820 — multi-branch) */}
        <Section title="Organization">
          <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-soft)', lineHeight: 1.5 }}>
            Is this pharmacy part of a chain or healthcare system that operates multiple branches under shared ownership? Branches of the same organization can share patient records.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" name="parentOrgMode" value="standalone"
                checked={form.parentOrgMode === 'standalone'}
                onChange={() => set('parentOrgMode', 'standalone')} />
              Standalone pharmacy (no parent organization)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" name="parentOrgMode" value="existing"
                checked={form.parentOrgMode === 'existing'}
                onChange={() => set('parentOrgMode', 'existing')}
                disabled={parentOrgs.length === 0} />
              Branch of an existing organization {parentOrgs.length === 0 && <span style={{ color: 'var(--color-text-mute)', fontSize: 11 }}>(none yet)</span>}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" name="parentOrgMode" value="new"
                checked={form.parentOrgMode === 'new'}
                onChange={() => set('parentOrgMode', 'new')} />
              First branch — create a new organization
            </label>
          </div>

          {form.parentOrgMode === 'existing' && (
            <Field label="Organization">
              <select value={form.parentOrganizationId} onChange={e => set('parentOrganizationId', e.target.value)} style={inputStyle}>
                <option value="">Select an organization…</option>
                {parentOrgs.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.display_name} ({o.branch_count ?? 0} branch{o.branch_count === 1 ? '' : 'es'})
                  </option>
                ))}
              </select>
            </Field>
          )}
          {form.parentOrgMode === 'new' && (
            <Field label="Organization name *">
              <input value={form.newParentOrganizationName}
                onChange={e => set('newParentOrganizationName', e.target.value)}
                placeholder="Apple Specialty Holdings" style={inputStyle} />
            </Field>
          )}
          {form.parentOrgMode !== 'standalone' && (
            <Field label="Branch label">
              <input value={form.branchLabel} onChange={e => set('branchLabel', e.target.value)}
                placeholder="Cupertino" style={inputStyle} />
            </Field>
          )}
        </Section>

        {/* Pharmacy details */}
        <Section title="Pharmacy details">
          <Field label="Pharmacy name *" required>
            <input value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="Apple Specialty Pharmacy" style={inputStyle} />
          </Field>
          <Row>
            <Field label="NPI">
              <input value={form.npi} onChange={e => set('npi', e.target.value)}
                placeholder="1234567890" style={inputStyle} />
            </Field>
            <Field label="NCPDP">
              <input value={form.ncpdp} onChange={e => set('ncpdp', e.target.value)}
                placeholder="1234567" style={inputStyle} />
            </Field>
            <Field label="DEA">
              <input value={form.dea} onChange={e => set('dea', e.target.value)}
                placeholder="AB1234567" style={inputStyle} />
            </Field>
          </Row>
          <Row>
            <Field label="Primary email">
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                placeholder="contact@pharmacy.example" style={inputStyle} />
            </Field>
            <Field label="Primary phone">
              <input type="tel" value={form.primaryPhone} onChange={e => set('primaryPhone', e.target.value)}
                placeholder="+1 555 123 4567" style={inputStyle} />
            </Field>
          </Row>
          <Field label="Address">
            <input value={form.addressLine} onChange={e => set('addressLine', e.target.value)}
              placeholder="123 Main St, Suite 200" style={inputStyle} />
          </Field>
          <Row>
            <Field label="City">
              <input value={form.city} onChange={e => set('city', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="State">
              <input value={form.stateCode} onChange={e => set('stateCode', e.target.value.toUpperCase())}
                placeholder="CA" maxLength={2} style={{ ...inputStyle, maxWidth: 80 }} />
            </Field>
            <Field label="Zip">
              <input value={form.zipCode} onChange={e => set('zipCode', e.target.value)}
                style={{ ...inputStyle, maxWidth: 120 }} />
            </Field>
          </Row>
        </Section>

        {/* BAA */}
        <Section title="Business Associate Agreement" tone="compliance">
          <p style={complianceNoteStyle}>
            <ShieldCheck size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />
            A signed BAA between Owl Consultations and this pharmacy must be in
            place before onboarding. Reference the contract id or document
            number below — it appears in the audit log.
          </p>
          <Field label="BAA reference *" required>
            <input value={form.baaReference} onChange={e => set('baaReference', e.target.value)}
              placeholder="OWL-BAA-2026-0001" style={inputStyle} />
          </Field>
          <Row>
            <Field label="BAA effective date">
              <input type="date" value={form.baaEffectiveDate}
                onChange={e => set('baaEffectiveDate', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="BAA expiry date">
              <input type="date" value={form.baaExpiryDate}
                onChange={e => set('baaExpiryDate', e.target.value)} style={inputStyle} />
            </Field>
          </Row>
        </Section>

        {/* COO approval */}
        <Section title="Pharmacy approval" tone="compliance">
          <p style={complianceNoteStyle}>
            Record the name and email of the COO (or other authorized signer)
            who approved this engagement. This is recorded with a timestamp in
            the audit log and cannot be modified after creation.
          </p>
          <Row>
            <Field label="Approver name *" required>
              <input value={form.cooApproverName} onChange={e => set('cooApproverName', e.target.value)}
                placeholder="Jane Doe" style={inputStyle} />
            </Field>
            <Field label="Approver email *" required>
              <input type="email" value={form.cooApproverEmail}
                onChange={e => set('cooApproverEmail', e.target.value)}
                placeholder="jdoe@pharmacy.example" style={inputStyle} />
            </Field>
          </Row>
        </Section>

        {/* SSO */}
        <Section title="Single sign-on (optional)">
          <Field label="SSO provider">
            <select value={form.ssoProvider}
              onChange={e => set('ssoProvider', e.target.value as typeof form.ssoProvider)}
              style={inputStyle}>
              <option value="none">None — pharmacy admin sets passwords manually</option>
              <option value="okta">Okta</option>
              <option value="azure_ad">Microsoft Entra (Azure AD)</option>
              <option value="google_workspace">Google Workspace</option>
            </select>
          </Field>
          {form.ssoProvider !== 'none' && (
            <p style={ssoNoteStyle}>
              SSO selected. The actual provider configuration (issuer URL,
              client id, redirect URIs) happens after pharmacy creation on the
              detail page. The pharmacy admin you invite next will need their
              identity provider's credentials.
            </p>
          )}
        </Section>

        <Section title="Sending domain (optional)">
          <p style={{ fontSize: 12.5, color: 'var(--color-text-mute)', margin: '0 0 10px', lineHeight: 1.5 }}>
            The domain this pharmacy's notification emails are sent from. Leave blank to decide later.
            Enter their domain to start setup now — after creating, you'll get the DNS records to publish,
            and once verified their mail sends from their own domain (DKIM-signed).
          </p>
          <Field label="Domain">
            <input value={form.sendingDomain} onChange={e => set('sendingDomain', e.target.value)}
              placeholder="e.g. mail.acmepharmacy.com" style={inputStyle} />
          </Field>
        </Section>

        {error && <div style={errorBox}><AlertCircle size={14} /> {error}</div>}


        {/* WI-897: tell the operator which fields are blocking submit
            instead of leaving them with a silently-disabled button */}
        {missing.length > 0 && !submitting && (
          <div style={hintBoxStyle}>
            <AlertCircle size={14} aria-hidden="true" />
            <div>
              <strong>Required to create:</strong>{' '}
              {missing.join(' · ')}
            </div>
          </div>
        )}

        <div style={actionsStyle}>
          <button type="button" onClick={() => navigate('/admin/pharmacies')}
            disabled={submitting} style={secondaryBtnStyle}>
            Cancel
          </button>
          <button type="submit" disabled={!canSubmit} style={primaryBtnStyle}>
            {submitting && <Loader2 size={13} className="spin" />}
            Create pharmacy
          </button>
        </div>
      </form>
    </div>
  )
}

function Section({ title, tone, children }: { title: string; tone?: 'compliance'; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-surface, #fff)',
      border: tone === 'compliance' ? '1px solid #fbbf24' : '1px solid var(--color-border)',
      borderRadius: 8,
      padding: 16,
      marginBottom: 12,
    }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px',
        color: tone === 'compliance' ? '#854d0e' : undefined }}>{title}</h3>
      {children}
    </div>
  )
}
function Field({ label, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--color-text-soft)' }}>{label}</div>
      {children}
    </label>
  )
}
function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>{children}</div>
}

const backLinkStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-soft)', fontSize: 13, padding: '4px 0', marginBottom: 12,
  fontFamily: 'inherit',
}
const h2Style: React.CSSProperties = { fontSize: 22, fontWeight: 700, margin: '0 0 4px', fontFamily: 'Georgia, serif' }
const subtitleStyle: React.CSSProperties = { margin: '0 0 20px', color: 'var(--color-text-soft)', fontSize: 13, maxWidth: 600 }
const complianceNoteStyle: React.CSSProperties = {
  margin: '0 0 12px', padding: '8px 12px',
  background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4,
  fontSize: 12, color: '#854d0e', lineHeight: 1.5,
}
const ssoNoteStyle: React.CSSProperties = {
  margin: '8px 0 0', padding: '8px 12px',
  background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 4,
  fontSize: 12, color: '#5b21b6', lineHeight: 1.5,
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-surface)', fontFamily: 'inherit', boxSizing: 'border-box',
}
const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
  background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12, marginTop: 8,
}
const hintBoxStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 12px',
  background: '#fef3c7', color: '#78350f', borderRadius: 4,
  fontSize: 12, marginTop: 8, lineHeight: 1.4,
}
const actionsStyle: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }
const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '10px 18px', fontSize: 13, fontWeight: 500,
  background: 'var(--color-primary, #0d9488)', color: '#fff', border: 'none',
  borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
}
const secondaryBtnStyle: React.CSSProperties = {
  ...primaryBtnStyle, background: 'transparent', color: 'var(--color-text-soft)',
  border: '1px solid var(--color-border)',
}
